// TrainingDetail — full detail page for a single Learning/Education resource.
//
// Mirrors frontend/src/features/posting-engagement/PostDetail.tsx pixel-for-
// pixel (same post-detail.css classes: detail-wrap, detail-card, detail-meta,
// detail-byline, detail-actions, pd-comments, pd-comment, …) so a Learning
// resource opens into the same kind of page as a Feed/Project post instead of
// staying "just a tile": back link → resource content → vote/share/reactions
// → comment thread. Replaces the old TrainingDetailModal (view + admin edit
// popup) that EducationPage used before this feature.
//
// The resource-specific content block (inline video/audio player, non-media
// attachment row, link-preview card, or plain link) reuses the same render
// branches as EducationPage.tsx's TrainingCard, and its CSS classes
// (education-card__video, __audio, __attachment, __preview*).
//
// Admin edit reuses the same field set as the old TrainingDetailModal's edit
// form (title/tags/url/file/description/featured) via updateTraining /
// uploadTrainingFile, exported from EducationPage.tsx.

import { type FormEvent, useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/api-client";
import {
  Avatar,
  Icon,
  TYPE_META,
  timeAgo as timeAgoAtom,
} from "@/features/hub-feed/design-atoms";
import { useAuthors } from "@/features/hub-feed/useAuthors";
import { useProfile } from "@/lib/useProfile";
import { toggleVote } from "@/features/posting-engagement/VoteControl";
import { ReactionBar } from "@/features/posting-engagement/ReactionBar";
import type { ReactionSummary } from "@/features/posting-engagement/reactions-api";
import { GifPicker } from "@/features/posting-engagement/GifPicker";
import type { GifResult } from "@/features/posting-engagement/gifs-api";
import { copyText } from "@/lib/clipboard";
import { isSafeHttpUrl } from "@/lib/url";
import {
  CONTENT_FORMATS,
  UPLOAD_ACCEPT,
  isLikelyTooLargeForUpload,
  updateTraining,
  uploadTrainingFile,
  type TrainingResourceItem,
} from "./EducationPage";
import {
  createTrainingComment,
  deleteTrainingComment,
  editTrainingComment,
  type TrainingCommentItem,
} from "./training-comments-api";
import "@/features/posting-engagement/post-detail.css";
import "./education-page.css";

// ---------------------------------------------------------------------------
// API types (mirrors api/schemas/training.py TrainingDetailResponse)
// ---------------------------------------------------------------------------

export interface TrainingDetailData extends TrainingResourceItem {
  /** True when the current user has upvoted this resource. */
  voted: boolean;
  reactions: ReactionSummary[];
  comments: TrainingCommentItem[];
}

// ---------------------------------------------------------------------------
// API fetch helper
// ---------------------------------------------------------------------------

export async function fetchTrainingDetail(resourceId: string): Promise<TrainingDetailData> {
  return apiClient<TrainingDetailData>(`/api/v1/training/${encodeURIComponent(resourceId)}`);
}

// ---------------------------------------------------------------------------
// Hook: useTrainingDetail
// ---------------------------------------------------------------------------

interface UseTrainingDetailResult {
  data: TrainingDetailData | null;
  loading: boolean;
  error: Error | null;
  notFound: boolean;
  reload: () => void;
}

export function useTrainingDetail(resourceId: string): UseTrainingDetailResult {
  const [data, setData] = useState<TrainingDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setNotFound(false);
    fetchTrainingDetail(resourceId)
      .then((result) => {
        setData(result);
      })
      .catch((e: unknown) => {
        if (e && typeof e === "object" && "status" in e && (e as { status: number }).status === 404) {
          setNotFound(true);
        } else {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [resourceId]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, notFound, reload: load };
}

// ---------------------------------------------------------------------------
// Not-found state
// ---------------------------------------------------------------------------

function TrainingNotFound() {
  return (
    <div className="pd-not-found" data-testid="training-not-found" role="alert">
      <h2 className="pd-not-found__heading">Resource not found</h2>
      <p className="pd-not-found__body">
        This learning resource does not exist or may have been removed.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TrainingResourceContent — the video/audio/attachment/link-preview block.
// Mirrors the render branches in EducationPage.tsx's TrainingCard.
// ---------------------------------------------------------------------------

function TrainingResourceContent({ resource }: { resource: TrainingDetailData }) {
  const [previewImgFailed, setPreviewImgFailed] = useState(false);

  const fileUrl = resource.file_url ?? null;
  const contentType = resource.file_content_type ?? "";
  const isVideo = fileUrl != null && contentType.startsWith("video/");
  const isAudio = fileUrl != null && contentType.startsWith("audio/");
  const isOtherFile = fileUrl != null && !isVideo && !isAudio;

  const safeUrl = isSafeHttpUrl(resource.url) ? resource.url : null;
  const hasOgPreview =
    !fileUrl &&
    !!safeUrl &&
    !!(resource.og_title || resource.og_description || resource.og_image_url);
  const plainLinkUrl = !fileUrl && !hasOgPreview ? safeUrl : null;

  return (
    <>
      {isVideo && (
        <video
          controls
          className="education-card__video"
          src={fileUrl as string}
          data-testid="training-video"
        />
      )}

      {isAudio && (
        <audio
          controls
          className="education-card__audio"
          src={fileUrl as string}
          data-testid="training-audio"
        />
      )}

      {isOtherFile && (
        <a
          className="education-card__attachment"
          href={fileUrl as string}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="training-attachment"
        >
          <span className="education-card__attachment-name">
            {resource.file_name ?? "Attachment"}
          </span>
          <span className="education-card__attachment-open">Open</span>
        </a>
      )}

      {hasOgPreview && (
        <a
          className="education-card__preview"
          href={safeUrl as string}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="training-link-preview"
        >
          {resource.og_image_url && !previewImgFailed && isSafeHttpUrl(resource.og_image_url) && (
            <img
              className="education-card__preview-image"
              src={resource.og_image_url}
              alt=""
              loading="lazy"
              onError={() => setPreviewImgFailed(true)}
            />
          )}
          <span className="education-card__preview-body">
            <span className="education-card__preview-site">
              {resource.og_site_name || new URL(safeUrl as string).hostname}
            </span>
            <span className="education-card__preview-title">
              {resource.og_title || safeUrl}
            </span>
          </span>
        </a>
      )}

      {plainLinkUrl && (
        <a
          className="detail-linkcard"
          href={plainLinkUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="training-external-link"
        >
          <span style={{ color: "var(--green-deep)" }}>
            <Icon.link size={20} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="pd-u">{plainLinkUrl}</div>
            <div className="pd-l">External link · opens in a new tab</div>
          </div>
          <Icon.external size={17} />
        </a>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// TrainingEditForm — admin-only inline edit, replaces TrainingDetailModal's
// edit branch. Same fields, same updateTraining/uploadTrainingFile calls.
// ---------------------------------------------------------------------------

function TrainingEditForm({
  resource,
  onSaved,
  onCancel,
}: {
  resource: TrainingDetailData;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(resource.title);
  const [tags, setTags] = useState(resource.tags.join(", "));
  const [url, setUrl] = useState(resource.url ?? "");
  const [description, setDescription] = useState(resource.description ?? "");
  const [contentFormat, setContentFormat] = useState(resource.content_format ?? "");
  const [series, setSeries] = useState(resource.series ?? "");
  const [seriesOrder, setSeriesOrder] = useState(
    resource.series_order != null ? String(resource.series_order) : "",
  );
  const [postedToFeed, setPostedToFeed] = useState(resource.posted_to_feed);
  const [file, setFile] = useState<File | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (submitting) return;
      setSubmitting(true);
      setError(null);
      try {
        const parsedTags = tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
        const updated = await updateTraining(resource.id, {
          title: title.trim(),
          tags: parsedTags,
          url: url.trim() || null,
          description: description.trim() || null,
          content_format: contentFormat || null,
          series: series.trim() || null,
          series_order: seriesOrder.trim() ? Number(seriesOrder) : null,
          posted_to_feed: postedToFeed,
        });
        if (file) {
          await uploadTrainingFile(updated.id, file);
        }
        onSaved();
      } catch (err: unknown) {
        const status =
          err && typeof err === "object" && "status" in err
            ? (err as { status: number }).status
            : 0;
        if (status === 403) {
          setError("Only admins can edit learning resources.");
        } else if (status === 415) {
          setError("That file type isn't supported. Accepted: video, audio, or PDF.");
        } else if (status === 413) {
          setError("That file is too large.");
        } else if (status === 422) {
          setError("Please check your input — some fields are invalid.");
        } else if (status === 404) {
          setError("This resource no longer exists.");
        } else {
          setError("Could not save changes. Please try again.");
        }
      } finally {
        setSubmitting(false);
      }
    },
    [
      submitting,
      title,
      tags,
      url,
      description,
      contentFormat,
      series,
      seriesOrder,
      postedToFeed,
      file,
      resource.id,
      onSaved,
    ],
  );

  return (
    <div className="hub-compose-wrap">
      {error && (
        <div className="hub-compose-error" role="alert" aria-live="assertive">
          {error}
        </div>
      )}
      <form className="hub-compose-form" onSubmit={handleSubmit} aria-label="Edit learning resource">
        <div className="hub-compose-field">
          <label htmlFor="edit-learn-title" className="hub-compose-label">
            Title <span aria-hidden="true">*</span>
          </label>
          <input
            id="edit-learn-title"
            type="text"
            className="hub-compose-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={512}
            disabled={submitting}
            aria-required="true"
          />
        </div>

        <div className="hub-compose-field">
          <label htmlFor="edit-learn-tags" className="hub-compose-label">
            Tags <span className="hub-compose-label--hint">(optional, comma-separated)</span>
          </label>
          <input
            id="edit-learn-tags"
            type="text"
            className="hub-compose-input"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className="hub-compose-field">
          <label htmlFor="edit-learn-url" className="hub-compose-label">
            URL <span className="hub-compose-label--hint">(optional — we'll fetch a preview)</span>
          </label>
          <input
            id="edit-learn-url"
            type="url"
            className="hub-compose-input"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className="hub-compose-field">
          <label htmlFor="edit-learn-format" className="hub-compose-label">
            Format <span className="hub-compose-label--hint">(optional)</span>
          </label>
          <select
            id="edit-learn-format"
            className="hub-compose-input"
            value={contentFormat}
            onChange={(e) => setContentFormat(e.target.value)}
            disabled={submitting}
          >
            <option value="">Auto (from content)</option>
            {CONTENT_FORMATS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        <div className="hub-compose-field">
          <label htmlFor="edit-learn-file" className="hub-compose-label">
            Replace upload <span className="hub-compose-label--hint">(optional — video, audio, or PDF)</span>
          </label>
          <input
            id="edit-learn-file"
            type="file"
            className="hub-compose-input"
            accept={UPLOAD_ACCEPT}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={submitting}
          />
          {isLikelyTooLargeForUpload(file) && (
            <p className="hub-compose-warning" data-testid="edit-learn-file-large-warning">
              Large files can fail to upload. For recordings like this, paste
              a link (SharePoint, Stream, Zoom cloud, etc.) in the URL field
              above instead.
            </p>
          )}
        </div>

        <div className="hub-compose-field">
          <label htmlFor="edit-learn-desc" className="hub-compose-label">
            Description <span className="hub-compose-label--hint">(optional)</span>
          </label>
          <textarea
            id="edit-learn-desc"
            className="hub-compose-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            disabled={submitting}
          />
        </div>

        <div className="hub-compose-field">
          <label htmlFor="edit-learn-series" className="hub-compose-label">
            Series{" "}
            <span className="hub-compose-label--hint">
              (optional — group multiple sessions into one program)
            </span>
          </label>
          <input
            id="edit-learn-series"
            type="text"
            className="hub-compose-input"
            value={series}
            onChange={(e) => setSeries(e.target.value)}
            placeholder="e.g. MCP Series"
            maxLength={256}
            disabled={submitting}
          />
        </div>

        {series.trim() && (
          <div className="hub-compose-field">
            <label htmlFor="edit-learn-series-order" className="hub-compose-label">
              Session number{" "}
              <span className="hub-compose-label--hint">(optional — orders the series)</span>
            </label>
            <input
              id="edit-learn-series-order"
              type="number"
              min={0}
              className="hub-compose-input"
              value={seriesOrder}
              onChange={(e) => setSeriesOrder(e.target.value)}
              placeholder="e.g. 1"
              disabled={submitting}
            />
          </div>
        )}

        <div className="hub-compose-field">
          <label className="hub-compose-label">
            <input
              type="checkbox"
              checked={postedToFeed}
              onChange={(e) => setPostedToFeed(e.target.checked)}
              disabled={submitting}
            />{" "}
            Post to feed
          </label>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="submit"
            className="hub-compose-submit"
            disabled={submitting}
            aria-disabled={submitting}
            data-testid="training-edit-save"
          >
            {submitting ? "Saving…" : "Save changes"}
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={onCancel}
            disabled={submitting}
            data-testid="training-edit-cancel"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TrainingDetail — main component
// ---------------------------------------------------------------------------

interface Props {
  /** The training resource id to fetch and render. */
  resourceId: string;
  /** Called when the user clicks "Back to the hub". */
  onBack?: () => void;
}

export function TrainingDetail({ resourceId, onBack }: Props) {
  const { data: resource, loading, error, notFound, reload } = useTrainingDetail(resourceId);
  const authors = useAuthors();
  const { isAdmin, profile } = useProfile();
  const currentUserId = profile?.id ?? null;

  const [editing, setEditing] = useState(false);

  const [voted, setVoted] = useState(false);
  const [voteCount, setVoteCount] = useState(0);
  const [voteBusy, setVoteBusy] = useState(false);

  const [comments, setComments] = useState<TrainingCommentItem[]>([]);
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [stagedGif, setStagedGif] = useState<GifResult | null>(null);

  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentDraft, setEditCommentDraft] = useState("");
  const [editCommentBusy, setEditCommentBusy] = useState(false);

  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);

  const [shared, setShared] = useState(false);

  useEffect(() => {
    if (resource) {
      setVoted(resource.voted);
      setVoteCount(resource.vote_count);
      setComments(resource.comments);
    }
  }, [resource]);

  const handleVote = useCallback(async () => {
    if (!resource || voteBusy) return;
    const prevVoted = voted;
    const prevCount = voteCount;
    const nextVoted = !voted;
    setVoted(nextVoted);
    setVoteCount(nextVoted ? voteCount + 1 : voteCount - 1);
    setVoteBusy(true);
    try {
      const res = await toggleVote("training", resource.id);
      setVoted(res.voted);
      setVoteCount(res.vote_count);
    } catch {
      setVoted(prevVoted);
      setVoteCount(prevCount);
    } finally {
      setVoteBusy(false);
    }
  }, [resource, voted, voteCount, voteBusy]);

  const handleShare = useCallback(async () => {
    if (!resource) return;
    const url = `${window.location.origin}/?training=${encodeURIComponent(resource.id)}`;
    if (await copyText(url)) {
      setShared(true);
      window.setTimeout(() => setShared(false), 1500);
    }
  }, [resource]);

  const submitComment = useCallback(async () => {
    if (!resource) return;
    const text = draft.trim();
    if ((!text && !stagedGif) || submitting) return;
    setSubmitting(true);
    try {
      const created = await createTrainingComment(resource.id, {
        text,
        gif_url: stagedGif?.url ?? null,
        gif_title: stagedGif?.title ?? null,
      });
      setComments((prev) => [
        ...prev,
        {
          id: created.id,
          author_id: created.author_id,
          author_name: created.author_name,
          author_initials: created.author_initials,
          author_division: created.author_division,
          text: created.text,
          vote_count: created.vote_count,
          created_at: created.created_at,
          gif_url: created.gif_url,
          gif_title: created.gif_title,
          reactions: created.reactions ?? [],
        },
      ]);
      setDraft("");
      setStagedGif(null);
    } catch {
      // Best-effort: leave the draft in place so the user can retry.
    } finally {
      setSubmitting(false);
    }
  }, [resource, draft, submitting, stagedGif]);

  const beginEditComment = useCallback((c: TrainingCommentItem) => {
    setEditingCommentId(c.id);
    setEditCommentDraft(c.text);
  }, []);

  const cancelEditComment = useCallback(() => {
    setEditingCommentId(null);
    setEditCommentDraft("");
  }, []);

  const saveEditComment = useCallback(
    async (c: TrainingCommentItem) => {
      if (!resource) return;
      const text = editCommentDraft.trim();
      if ((!text && !c.gif_url) || editCommentBusy) return;
      setEditCommentBusy(true);
      try {
        const updated = await editTrainingComment(resource.id, c.id, {
          text,
          gif_url: c.gif_url ?? null,
          gif_title: c.gif_title ?? null,
        });
        setComments((prev) =>
          prev.map((x) =>
            x.id === c.id ? { ...x, text: updated.text, updated_at: updated.updated_at } : x,
          ),
        );
        setEditingCommentId(null);
        setEditCommentDraft("");
      } catch {
        // Best-effort: keep the edit form open so the user can retry.
      } finally {
        setEditCommentBusy(false);
      }
    },
    [resource, editCommentDraft, editCommentBusy],
  );

  const handleDeleteComment = useCallback(
    async (c: TrainingCommentItem) => {
      if (!resource || deletingCommentId != null) return;
      if (!window.confirm("Delete this comment? This action cannot be undone.")) return;
      setDeletingCommentId(c.id);
      try {
        await deleteTrainingComment(resource.id, c.id);
        setComments((prev) => prev.filter((x) => x.id !== c.id));
        if (editingCommentId === c.id) {
          setEditingCommentId(null);
          setEditCommentDraft("");
        }
      } catch {
        // Best-effort: leave the comment in place so the user can retry.
      } finally {
        setDeletingCommentId(null);
      }
    },
    [resource, deletingCommentId, editingCommentId],
  );

  if (loading) {
    return (
      <div
        className="pd-loading"
        role="status"
        aria-live="polite"
        data-testid="training-detail-loading"
      >
        Loading resource…
      </div>
    );
  }

  if (notFound) {
    return <TrainingNotFound />;
  }

  if (error || !resource) {
    return (
      <div className="pd-error" role="alert" data-testid="training-detail-error">
        <strong>Could not load this resource.</strong>
        <p>{error?.message ?? "An unexpected error occurred."}</p>
      </div>
    );
  }

  const author = resource.added_by_id ? authors[resource.added_by_id] : undefined;
  const authorName = author?.name ?? resource.added_by_id ?? "Unknown";
  const avatarId = resource.added_by_id ?? "anon";

  if (editing) {
    return (
      <div className="detail-wrap fade-in" data-testid="training-detail">
        <button type="button" className="backlink" onClick={onBack} data-testid="back-link">
          <Icon.arrowL size={16} /> Back to the hub
        </button>
        <div className="detail-card" data-testid="training-edit-card">
          <h2 style={{ marginTop: 0 }}>Edit learning resource</h2>
          <TrainingEditForm
            resource={resource}
            onSaved={() => {
              setEditing(false);
              reload();
            }}
            onCancel={() => setEditing(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="detail-wrap fade-in" data-testid="training-detail">
      <button type="button" className="backlink" onClick={onBack} data-testid="back-link">
        <Icon.arrowL size={16} /> Back to the hub
      </button>

      <article className="detail-card" data-testid="training-detail-card">
        <div className="detail-meta">
          <span className="pd-tbadge" aria-label={`Type: ${TYPE_META.learning.label}`}>
            <Icon.book size={14} /> {TYPE_META.learning.label}
          </span>
          {resource.posted_to_feed && (
            <span className="education-card__feed-badge" data-testid="feed-badge">
              In Feed
            </span>
          )}
          {isAdmin && (
            <button
              type="button"
              className="pd-edit-btn"
              data-testid="training-edit-button"
              onClick={() => setEditing(true)}
              aria-label="Edit this resource"
            >
              Edit
            </button>
          )}
        </div>

        <h1 data-testid="training-title">{resource.title}</h1>

        <div className="detail-byline" data-testid="training-byline">
          <Avatar id={avatarId} initials={author?.initials} name={author?.name} avatar={author?.avatar} size={42} />
          <div style={{ lineHeight: 1.25, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap" }}>
              {authorName}
            </div>
            <div style={{ fontSize: 12, color: "var(--muted)", whiteSpace: "nowrap" }}>
              {resource.created_at && timeAgoAtom(resource.created_at)}
            </div>
          </div>
        </div>

        {resource.description && (
          <p className="education-detail__description">{resource.description}</p>
        )}

        <TrainingResourceContent resource={resource} />

        {resource.tags.length > 0 && (
          <div className="education-card__tags" style={{ marginTop: 20 }} data-testid="training-tags">
            {resource.tags.map((t) => (
              <span key={t} className="hub-tag">
                {t}
              </span>
            ))}
          </div>
        )}

        <div className="detail-actions" data-testid="training-actions">
          <button
            type="button"
            className={"pd-actbtn" + (voted ? " on" : "")}
            onClick={handleVote}
            disabled={voteBusy}
            aria-pressed={voted}
            aria-label={`${voteCount} upvotes`}
          >
            <Icon.up size={15} /> {voteCount} {voteCount === 1 ? "upvote" : "upvotes"}
          </button>
          <button
            type="button"
            className={"pd-actbtn" + (shared ? " on" : "")}
            aria-label="Share this resource"
            onClick={handleShare}
          >
            <Icon.share size={15} /> {shared ? "Copied!" : "Share"}
          </button>
          <ReactionBar target="training" targetId={resource.id} initialReactions={resource.reactions} />
        </div>
      </article>

      <section className="pd-comments" data-testid="comments-section" aria-label="Comments">
        <h3>
          Comments <span className="pd-n">{comments.length}</span>
        </h3>

        <div className="pd-cform">
          <Avatar id={avatarId} initials={author?.initials} name={author?.name} avatar={author?.avatar} size={36} />
          <div style={{ flex: 1 }}>
            <textarea
              data-testid="comment-textarea"
              placeholder="Add a comment — share what you tried, ask a question…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submitComment();
              }}
            />
            {stagedGif && (
              <div className="gif-staged" data-testid="staged-gif">
                <img src={stagedGif.preview_url} alt={stagedGif.title} />
                <button
                  type="button"
                  className="gif-staged__remove"
                  aria-label="Remove attached GIF"
                  onClick={() => setStagedGif(null)}
                  data-testid="staged-gif-remove"
                >
                  ×
                </button>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 9 }}>
              <GifPicker onSelect={setStagedGif} />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={submitComment}
                disabled={(!draft.trim() && !stagedGif) || submitting}
                data-testid="comment-submit"
                style={{ opacity: (draft.trim() || stagedGif) && !submitting ? 1 : 0.5 }}
              >
                {submitting ? "Posting…" : "Comment"}
              </button>
            </div>
          </div>
        </div>

        {comments.map((c) => {
          const cName = c.author_name ?? c.author_id ?? "Unknown";
          const canEditComment =
            isAdmin || (currentUserId != null && currentUserId === c.author_id);
          const isEditingThis = editingCommentId === c.id;
          return (
            <div className="pd-comment" data-testid="comment-row" data-comment-id={c.id} key={c.id}>
              <Avatar
                id={c.author_id ?? "anon"}
                initials={c.author_initials ?? undefined}
                name={c.author_name ?? undefined}
                size={36}
              />
              <div>
                <div className="cmeta">
                  <b>{cName}</b>
                  {c.author_division && <span className="t">{c.author_division}</span>}
                  <span className="t">{timeAgoAtom(c.created_at)}</span>
                  {c.updated_at && (
                    <span className="t" data-testid="comment-edited">
                      {" "}
                      · edited
                    </span>
                  )}
                </div>
                {isEditingThis ? (
                  <div className="pd-comment-edit" data-testid="comment-edit-form">
                    <textarea
                      className="pd-comment-edit__input"
                      data-testid="comment-edit-textarea"
                      value={editCommentDraft}
                      onChange={(e) => setEditCommentDraft(e.target.value)}
                      rows={3}
                      aria-label="Edit comment"
                    />
                    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => saveEditComment(c)}
                        disabled={(!editCommentDraft.trim() && !c.gif_url) || editCommentBusy}
                        data-testid="comment-edit-save"
                      >
                        {editCommentBusy ? "Saving…" : "Save"}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={cancelEditComment}
                        disabled={editCommentBusy}
                        data-testid="comment-edit-cancel"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  c.text && <div className="ctext">{c.text}</div>
                )}
                {c.gif_url && isSafeHttpUrl(c.gif_url) && (
                  <img
                    className="pd-comment-gif"
                    src={c.gif_url}
                    alt={c.gif_title ?? "Attached GIF"}
                    loading="lazy"
                    data-testid="comment-gif"
                  />
                )}
                <div className="cfoot">
                  <span className="pd-cvote" aria-label={`${c.vote_count} votes`}>
                    <Icon.up size={12} /> {c.vote_count}
                  </span>
                  <ReactionBar
                    target="training_comment"
                    targetId={String(c.id)}
                    initialReactions={c.reactions ?? []}
                  />
                  {canEditComment && !isEditingThis && (
                    <button
                      type="button"
                      className="pd-comment-edit-btn"
                      data-testid="comment-edit-button"
                      onClick={() => beginEditComment(c)}
                      aria-label="Edit this comment"
                    >
                      Edit
                    </button>
                  )}
                  {canEditComment && (
                    <button
                      type="button"
                      className="pd-comment-edit-btn"
                      data-testid="comment-delete-button"
                      onClick={() => handleDeleteComment(c)}
                      disabled={deletingCommentId === c.id}
                      aria-label="Delete this comment"
                    >
                      {deletingCommentId === c.id ? "Deleting…" : "Delete"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
}

export default TrainingDetail;
